import React, { useState, useEffect, useRef } from "react";
import {
  createInvoice,
  getInvoicesByEndcustomerIdV2,
  getClient,
} from "API/swesum";

import { getMyEndcustomerInvoices as APIGetMyEndCustomerInvoices } from "API/myPages";

import useInvoiceFormOptions from "Components/Invoice/useInvoiceFormOptions";
import usePost from "Hooks/usePost";
import dayjs from "dayjs";
import DateWithClear from "Components/UI/Form/DateWithClear";
import { Grid } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { openStatusOptions } from "utils/options/openStatusOptions";
import { SUB_STATUS } from "utils/options/SUB_STATUS";
import MenuItem from "@mui/material/MenuItem";
import InvoiceTable from "Pages/AdminInterface/clients/client/invoices/components/InvoiceTable";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import CreateInvoiceModal from "Components/UI/Modals/CreateInvoiceModal";
import { useAuth } from "Hooks/useAuth";
import createFilters from "utils/invoicesFilterParser";

export default function EndcustomerInvoices({
  endcustomer,
  endcustomerArticleMap,
}) {
  const { isAdmin } = useAuth();
  const { openSuccess, openError } = usePopupNotificationContext();
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const handleOpenCreateInvoice = () => setCreateInvoiceModalOpen(true);
  const handleCloseCreateInvoice = () => setCreateInvoiceModalOpen(false);
  const {
    loading,
    options,
    formData: invoiceFormData,
    handleInputChange,
  } = useInvoiceFormOptions();

  const [invoices, setInvoices] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openStatus, setOpenStatus] = useState(0);
  const [subStatus, setSubStatus] = useState(-1);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalRows, setTotalRows] = useState(30);
  const [order, setOrder] = useState({ Column: "InvoiceDate", Order: 1 });
  const [searchTerm, setSearchTerm] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [client, setClient] = useState(null);

  const prevFilters = useRef({
    startDate,
    endDate,
    endcustomer,
    openStatus,
    subStatus,
  });

  invoiceFormData.EndCustomerId = endcustomer.Id;

  const createInvoiceReq = usePost({
    mtnFunc: (invoiceFormData) => createInvoice(invoiceFormData),
    onError: () => {
      openError("Ett fel uppstod när fakturan skapades.", 3000);
    },
    onSuccess: (data) => {
      openSuccess("Fakturan har skapats.", 3000);
      setInvoices([data, ...invoices]);
    },
  });

  const saveInvoice = () => {
    handleCloseCreateInvoice();
    createInvoiceReq.post(invoiceFormData);
  };

  const onDownload = async () => {
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        searchTerm: null,
      });
      const body = {
        Filters: filters,
        Order: order,
        Page: currentPage,
        PageSize: 0,
      };
      const newInvoices = isAdmin
        ? await getInvoicesByEndcustomerIdV2(endcustomer.Id, body)
        : await APIGetMyEndCustomerInvoices(endcustomer.Id, body);
      return newInvoices;
    } catch (err) {
      openError("Ett fel uppstod", 3000);
    }
  };

  const getInvoicesByEndcustomerId = async (
    force = false,
    searchTerm = null,
    pageOverride = null
  ) => {
    setLoadingInvoices(true);
    if (searchTerm) setSearchTerm(searchTerm);
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        searchTerm: searchTerm,
      });

      const body = {
        Filters: filters,
        Order: order,
        Page: pageOverride !== null ? pageOverride : currentPage,
        PageSize: subStatus === -1 ? rowsPerPage : 0,
      };
      const newInvoices = isAdmin
        ? await getInvoicesByEndcustomerIdV2(endcustomer.Id, body)
        : await APIGetMyEndCustomerInvoices(endcustomer.Id, body);

      setTotalRows(newInvoices.Count);
      if (force) {
        setInvoices(newInvoices.Rows);
      } else {
        const invoiceMap = new Map(
          newInvoices.Rows.map((invoice) => [invoice.Id, invoice])
        );
        const mergedInvoices = [
          ...new Map(
            [
              ...invoices.filter((invoice) => !invoiceMap.has(invoice.Id)),
              ...newInvoices.Rows,
            ].map((invoice) => [invoice.Id, invoice])
          ).values(),
        ];

        setInvoices(mergedInvoices);
      }
    } catch (err) {
      openError("Ett fel uppstod", 2000);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const updateRows = () => {
    getInvoicesByEndcustomerId();
  };

  useEffect(() => {
    const filtersChanged =
      prevFilters.current.startDate !== startDate ||
      prevFilters.current.endDate !== endDate ||
      prevFilters.current.endcustomer !== endcustomer ||
      prevFilters.current.openStatus !== openStatus ||
      prevFilters.current.subStatus !== subStatus;

    const getInvoicesReq = async () => {
      try {
        if (filtersChanged) {
          await getInvoicesByEndcustomerId(true, null, 0);
          setCurrentPage(0);
        } else {
          await getInvoicesByEndcustomerId(false);
        }
      } catch (err) {
        openError("Någonting gick fel vid hämtning av fakturor", 3000);
      }
    };

    prevFilters.current = {
      startDate,
      endDate,
      endcustomer,
      openStatus,
      subStatus,
    };

    getInvoicesReq();
  }, [
    rowsPerPage,
    currentPage,
    startDate,
    endDate,
    endcustomer,
    openStatus,
    order,
    subStatus,
  ]);

  useEffect(() => {
    const getEndcustomerClient = async () => {
      if (endcustomer?.ClientId) {
        const res = await getClient(endcustomer?.ClientId);
        setClient(res);
      }
    };

    if (isAdmin) getEndcustomerClient();
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <DateWithClear
            label="Från"
            value={startDate === "" ? "" : dayjs(startDate)}
            onChange={(newVal) => {
              setStartDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <DateWithClear
            label="Till"
            value={dayjs(endDate)}
            onChange={(newVal) => {
              setEndDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Stängd/Öppen</InputLabel>
            <Select
              name="OpenStatus"
              value={openStatus}
              onChange={(e) => setOpenStatus(e.target.value)}
              label="OpenStatus"
              size="small"
            >
              {openStatusOptions.map((options) => (
                <MenuItem key={options.Value} value={options.Value}>
                  {options.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Substatus</InputLabel>
            <Select
              name="Substatus"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value)}
              label="Substatus"
              size="small"
            >
              {SUB_STATUS.map((options) => (
                <MenuItem key={options.value} value={options.value}>
                  {options.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <InvoiceTable
        invoices={invoices}
        endcustomerArticleMap={endcustomerArticleMap}
        openCreateInvoice={handleOpenCreateInvoice}
        onChangePage={(pageNr) => {
          setLoadingInvoices(true);
          setCurrentPage(pageNr);
        }}
        onChangeRowsPerPage={(invoiceNumber) => setRowsPerPage(invoiceNumber)}
        onChangeSort={(column, order) => {
          setLoadingInvoices(true);
          setOrder({ Column: column, Order: order });
        }}
        sortOrder={order}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onUpdate={updateRows}
        onSearch={(searchTerm) => getInvoicesByEndcustomerId(true, searchTerm)}
        isEndcustomer
        getAllOnDownload={onDownload}
      />

      {isAdmin && !loading && options && (
        <CreateInvoiceModal
          handleClose={handleCloseCreateInvoice}
          isOpen={createInvoiceModalOpen}
          client={client}
          endCustomer={endcustomer}
        />
      )}
    </>
  );
}
